import React, { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { WTitle } from '../../components/Widget';
import { useUpdateComponentState } from '../../../hooks';
import Icon1 from '../../../../../../assets/2025/topicAI/icon1.png';
import Icon2 from '../../../../../../assets/2025/topicAI/icon2.png';
import Icon3 from '../../../../../../assets/2025/topicAI/icon3.png';
import ArrowImg from '../../../../../../assets/2025/arrow.gif';
import BgRiskImg from '../../../../../../assets/2025/topicAI/bg-risk.png';
import ImgSplice1 from '../../../../../../assets/2025/topicAI/img-splice-1.png';
import ImgSplice2 from '../../../../../../assets/2025/topicAI/img-splice-2.png';
import ImgSplice3 from '../../../../../../assets/2025/topicAI/img-splice-3.png';
import Section1Bg1 from '../../../../../../assets/2025/topicAI/section1-bg1.png';

interface ISection1Props {
  settings: any;
}

interface RiskItem {
  usage: string;
  risks: string[];
  tips: string[];
}

const Section1: React.FunctionComponent<ISection1Props> = ({ settings }) => {
  const ID = '2';
  const param = useParams();
  const p_course = param.course || '';
  const p_topic = param['*'] || 'welcome';
  const paragraphsSettings = useMemo(
    () => settings?.paragraphs[1],
    [settings?.paragraphs]
  );

  const columnConfig = [
    {
      type: 'usage',
      header: paragraphsSettings.aiRiskDataTitle[0],
      icon: Icon1,
    },
    {
      type: 'risk',
      header: paragraphsSettings.aiRiskDataTitle[1],
      icon: Icon2,
    },
    { type: 'tip', header: paragraphsSettings.aiRiskDataTitle[2], icon: Icon3 },
  ] as const;

  const [visibleCount, setVisibleCount] = useState(1);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeMobileTab, setActiveMobileTab] = useState<'risk' | 'tip'>(
    'risk'
  );

  const updateComponentState = useUpdateComponentState();

  const handleClick = () => {
    if (isExpanded) return;
    setIsExpanded(true);
    updateComponentState(p_course, p_topic, ID, '2025-topicAI-section1-1');
    let count = 1;
    const interval = setInterval(() => {
      count++;
      setVisibleCount(count);
      if (count >= paragraphsSettings.aiRiskData.length) {
        clearInterval(interval);
      }
    }, 500);
  };

  const getCellContent = (item: RiskItem, type: string) => {
    if (type === 'usage') {
      return (
        <>
          <span className="usage-text">{item.usage}</span>
          <span className="usage-arrow">▶</span>
        </>
      );
    }
    const list = type === 'risk' ? item.risks : item.tips;
    return (
      <ul>
        {list.map((text, i) => (
          <li key={i}>{text}</li>
        ))}
      </ul>
    );
  };

  const getCardClassName = (colType: string, isFirstRow: boolean): string => {
    const classes = [`${colType}-card`];

    if (isFirstRow && colType === 'usage' && !isExpanded) {
      classes.push('clickable');
    }

    const isHidden =
      (!isFirstRow && colType === 'risk' && activeMobileTab === 'tip') ||
      (!isFirstRow && colType === 'tip' && activeMobileTab === 'risk');
    if (isHidden) classes.push('mobile-hide');

    const isContentHidden =
      isFirstRow &&
      (colType === 'risk' || colType === 'tip') &&
      ((colType === 'risk' && activeMobileTab === 'tip') ||
        (colType === 'tip' && activeMobileTab === 'risk'));
    if (isContentHidden) classes.push('content-hide');

    return classes.join(' ');
  };

  const renderMobileTabSwitcher = () => (
    <div className="mobile-tab-switcher">
      {(['risk', 'tip'] as const).map((tab) => (
        <div
          key={tab}
          className={`mobile-tab-item mobile-tab-${tab} ${
            activeMobileTab === tab ? 'active' : ''
          }`}
        >
          <div className="mobile-tab-icon">
            <img src={tab === 'risk' ? Icon2 : Icon3} alt="" />
          </div>
          <button
            type="button"
            className="mobile-tab-btn"
            onClick={() => setActiveMobileTab(tab)}
          >
            {paragraphsSettings.aiRiskDataTitle[tab === 'risk' ? 1 : 2]}
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="topic-section-1">
        <WTitle title={paragraphsSettings?.title}></WTitle>
        <div className="AI-risk-warning-container">
          {!isExpanded && (
            <img className="arrow-hint" src={ArrowImg} alt="點擊查看更多" />
          )}
          <img className="bg-risk-deco" src={BgRiskImg} alt="" />
          <div className="AI-risk-columns" id="2025-topicAI-section1-1">
            {paragraphsSettings.aiRiskData?.map(
              (item: RiskItem, index: number) => {
                const isFirstRow = index === 0;
                const isVisible = index < visibleCount;
                return (
                  <div
                    key={index}
                    className={`columns-row ${isFirstRow ? 'first-row' : ''} ${
                      isVisible ? 'visible' : ''
                    }`}
                    style={{ transitionDelay: `${index * 50}ms` }}
                  >
                    {columnConfig.map((col) => (
                      <div
                        key={col.type}
                        className={getCardClassName(col.type, isFirstRow)}
                        onClick={
                          isFirstRow && col.type === 'usage' && !isExpanded
                            ? handleClick
                            : undefined
                        }
                      >
                        {isFirstRow && (
                          <>
                            <div className="card-icon">
                              <img src={col.icon} alt={col.header} />
                            </div>
                            {col.type === 'risk' && renderMobileTabSwitcher()}
                            <div className="card-header">{col.header}</div>
                          </>
                        )}
                        <div className="card-content">
                          {getCellContent(item, col.type)}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
            )}
          </div>
        </div>

        <div className="AI-risk-real-case">
          <div className="real-case-bg-bar">
            <img src={ImgSplice1} alt="" />
          </div>
          <div className="real-case-header">
            <div className="real-case-title-wrap">
              <div className="real-case-title">
                {paragraphsSettings.realCaseTitle}
              </div>
            </div>
          </div>
          <div className="real-case-person">
            <img src={ImgSplice2} alt="" />
          </div>
          <div className="real-case-body">
            <div className="real-case-speech-bubble">
              <span className="bubble-arrow"></span>
              <p>{paragraphsSettings.realCaseData.speechBubble}</p>
            </div>
            <div className="real-case-paragraphs">
              {paragraphsSettings.realCaseData.paragraphs.map(
                (text: string, i: number) => (
                  <p key={i}>{text}</p>
                )
              )}
            </div>
            <div className="real-case-illustration">
              <img src={ImgSplice3} alt="" />
            </div>
          </div>
        </div>

        <div className="AI-intro-summary">
          <div className="intro-whiteboard-text">
            <p>{paragraphsSettings.AIIntroSummary.summary1}</p>
          </div>
          <div className="intro-orange-bubble">
            <p>{paragraphsSettings.AIIntroSummary.summary2}</p>
          </div>
          <img className="intro-bg" src={Section1Bg1} alt="" />
        </div>
      </div>
    </>
  );
};

export default Section1;
